import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { useNotification } from '../../context/NotificationContext';
import { formatXPUNT } from '../../utils/helpers';
import { 
  FaHome, FaTicketAlt, FaTrophy, FaHandshake, FaUser, 
  FaWallet, FaBell, FaCog, FaSignOutAlt, FaChartLine, 
  FaCrown, FaPlus, FaStar, FaBars, FaTimes,
  FaTachometerAlt, FaUsers, FaShieldAlt, FaRocket,
  FaChevronLeft, FaChevronRight, FaSearch,
  FaRegBell, FaRegUser, FaRegEnvelope
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import './DashboardLayout.css';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { balance } = useWallet();
  const { unreadCount } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isPunterPlus = user?.punter_plus || false;
  const isPunterPlusActive = user?.punter_plus && user?.punter_plus_status !== 'expired';
  const isAdmin = user?.capabilities?.manage_options || false;

  // Navigation items
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FaTachometerAlt },
    { path: '/bet-codes', label: 'Bet Codes', icon: FaTicketAlt },
    { path: '/competitions', label: 'Competitions', icon: FaTrophy },
    { path: '/h2h', label: 'H2H Games', icon: FaHandshake },
    { path: '/rollover', label: 'Rollover', icon: FaChartLine },
    { path: '/leaderboard', label: 'Leaderboard', icon: FaChartLine },
    { path: '/explore', label: 'Explore', icon: FaUsers },
  ];

  const bottomItems = [
    { path: '/wallet', label: 'Wallet', icon: FaWallet },
    { path: '/profile', label: 'Profile', icon: FaUser },
    { path: '/settings', label: 'Settings', icon: FaCog },
    { path: '/notifications', label: 'Notifications', icon: FaBell, badge: unreadCount },
  ];

  return (
    <div className="dashboard-layout">
      {/* Top Bar */}
      <header className="dashboard-topbar">
        <div className="topbar-left">
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
          <Link to="/" className="topbar-logo">
            <span className="logo-icon">
               <img src='/logo.png' style={{width: 30, height: 30}}/>
            </span>
            <span className="logo-text">XPunters</span>
          </Link>
        </div>

        <div className="topbar-center">
          <div className="topbar-search">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
            />
          </div>
        </div>

        <div className="topbar-right">
          {/* Admin Button */}
          {isAdmin && (
            <Link to="/admin" className="topbar-btn admin-btn" title="Admin Dashboard">
              <FaShieldAlt />
            </Link>
          )}

          {/* Punter+ Button */}
          <Link to="/subscription" className="topbar-btn punter-btn">
            {isPunterPlusActive ? (
              <>
                <FaCrown className="crown-icon" />
                <span className="punter-label">Punter+</span>
              </>
            ) : (
              <>
                <FaStar className="star-icon" />
                <span className="punter-label">Upgrade</span>
              </>
            )}
          </Link>

          {/* Wallet */}
          <Link to="/wallet" className="topbar-btn wallet-btn">
            <FaWallet />
            <span className="wallet-balance">{formatXPUNT(balance)}</span>
          </Link>

          {/* Notifications */}
          <Link to="/notifications" className="topbar-btn notification-btn">
            <FaBell />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </Link>

          {/* User Avatar */}
          <div className="topbar-user">
            <button 
              className="user-avatar-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {user?.display_name?.[0] || 'U'}
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      <div className="dashboard-body">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <nav className="sidebar-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="sidebar-icon" />
                <span className="sidebar-label">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="sidebar-divider"></div>

          <nav className="sidebar-nav bottom">
            {bottomItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="sidebar-icon" />
                <span className="sidebar-label">{item.label}</span>
                {item.badge > 0 && (
                  <span className="sidebar-badge">{item.badge}</span>
                )}
              </Link>
            ))}

            {isPunterPlusActive && (
              <Link to="/creator/dashboard" className="sidebar-link creator-link">
                <FaRocket className="sidebar-icon" />
                <span className="sidebar-label">Creator</span>
              </Link>
            )}

            <button onClick={handleLogout} className="sidebar-link logout-link">
              <FaSignOutAlt className="sidebar-icon" />
              <span className="sidebar-label">Logout</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* Mobile Menu */}
      <div className={`mobile-dashboard-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-user-info">
            <div className="mobile-user-avatar">
              {user?.display_name?.[0] || 'U'}
            </div>
            <div className="mobile-user-details">
              <span className="mobile-user-name">{user?.display_name || 'User'}</span>
              <span className="mobile-user-email">{user?.email || ''}</span>
            </div>
          </div>
        </div>

        <nav className="mobile-menu-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-menu-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <item.icon className="mobile-menu-icon" />
              <span>{item.label}</span>
            </Link>
          ))}
          <div className="mobile-menu-divider"></div>
          {bottomItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-menu-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <item.icon className="mobile-menu-icon" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span className="mobile-menu-badge">{item.badge}</span>
              )}
            </Link>
          ))}
          {isPunterPlusActive && (
            <Link
              to="/creator/dashboard"
              className="mobile-menu-link creator-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaRocket className="mobile-menu-icon" />
              <span>Creator Dashboard</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              className="mobile-menu-link admin-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaShieldAlt className="mobile-menu-icon" />
              <span>Admin Dashboard</span>
            </Link>
          )}
          <button onClick={handleLogout} className="mobile-menu-link logout-link">
            <FaSignOutAlt className="mobile-menu-icon" />
            <span>Logout</span>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default DashboardLayout;